import { enumerateNodes, enumerateLeafs, isJoint, Joint, isLeaf, Leaf } from '@/core/helpers/tree';
import { Node } from '@/core/helpers/tree';
import { assertNever, guid } from '@/core/helpers/utils';
import { TileData, ContainerData, Box, Direction } from './types';

interface NewTileParams {
    key: string;
    name: string;
    args?: any;
}

interface Styles {
    top?: string;
    bottom?: string;
    left?: string;
    right?: string;
}

export function getBoxStyle(box: Box): Styles {
    return {
        top: box.top + '%',
        bottom: box.bottom + '%',
        left: box.left + '%',
        right: box.right + '%',
    };
}

export function getCssText(box: Box): string {
    let top: string = box.top % 1 !== 0 ? box.top.toFixed(4) : box.top.toString();
    while (+top % 1 !== 0 && top.substr(top.length - 1, 1) === '0') {
        top = top.slice(0, -1);
    }
    let bottom: string = box.bottom % 1 !== 0 ? box.bottom.toFixed(4) : box.bottom.toString();
    while (+bottom % 1 !== 0 && bottom.substr(bottom.length - 1, 1) === '0') {
        bottom = bottom.slice(0, -1);
    }
    let left: string = box.left % 1 !== 0 ? box.left.toFixed(4) : box.left.toString();
    while (+left % 1 !== 0 && left.substr(left.length - 1, 1) === '0') {
        left = left.slice(0, -1);
    }
    let right: string = box.right % 1 !== 0 ? box.right.toFixed(4) : box.right.toString();
    while (+right % 1 !== 0 && right.substr(right.length - 1, 1) === '0') {
        right = right.slice(0, -1);
    }
    return `top: ${top}%; bottom: ${bottom}%; left: ${left}%; right: ${right}%;`;
}

export function getBoxWidth(box: Box): number {
    return 100 - box.left - box.right;
}

export function getBoxHeight(box: Box): number {
    return 100 - box.top - box.bottom;
}

export function mergeBoxes(x: Box, y: Box): Box {
    return {
        left: Math.min(x.left, y.left),
        right: Math.min(x.right, y.right),
        top: Math.min(x.top, y.top),
        bottom: Math.min(x.bottom, y.bottom),
    };
}

export function updateSplitRatio(joint: Joint<TileData, ContainerData>, ratio: number): string[] {
    joint.data!.ratio = ratio;
    return updateBoundingBox(joint, joint.data!.boundingBox);
}

interface TreeOperationResult {
    root?: Node<TileData, ContainerData>;
    modifiedTile?: TileData;
    modifiedJoint?: Joint<TileData, ContainerData>;
}

export function expandTile(
    root: Node<TileData, ContainerData> | undefined,
    key: string,
): TreeOperationResult {
    if (root === undefined) {
        return {};
    }

    for (const node of enumerateNodes(root)) {
        if (isLeaf(node) && (node as Leaf<TileData>).key === key) {
            updateBoundingBox(node, createMaxBox());
            return { root: node, modifiedTile: node as Leaf<TileData> };
        }
    }

    throw new Error('Key not found');
}

export function deleteTile(
    root: Node<TileData, ContainerData> | undefined,
    key: string,
): TreeOperationResult {
    if (root === undefined) {
        return {};
    }

    if (isLeaf(root) && (root as Leaf<TileData>).key === key) {
        return { modifiedTile: root as Leaf<TileData> };
    }

    for (const node of enumerateNodes(root)) {
        if (isJoint(node)) {
            const currentJoint = node as Joint<TileData, ContainerData>;
            let removeLeaf: keyof Joint<TileData, ContainerData> | undefined;
            let popLeaf: keyof Joint<TileData, ContainerData> | undefined;

            if (isLeaf(currentJoint.left) && (currentJoint.left as Leaf<TileData>).key === key) {
                removeLeaf = 'left';
                popLeaf = 'right';
            } else if (isLeaf(currentJoint.right) && (currentJoint.right as Leaf<TileData>).key === key) {
                removeLeaf = 'right';
                popLeaf = 'left';
            }

            if (removeLeaf !== undefined) {
                const modifiedTile = currentJoint[removeLeaf] as Leaf<TileData>;
                const modifiedJoint = currentJoint;
                root = replaceJointWithLeaf(root, modifiedJoint, popLeaf!);
                return { root, modifiedTile, modifiedJoint };
            }
        }
    }

    return { root };
}

function replaceJointWithLeaf(
    root: Node<TileData, ContainerData>,
    target: Joint<TileData, ContainerData>,
    leafKey: keyof Joint<TileData, ContainerData>,
): Node<TileData, ContainerData> {
    const leaf = target[leafKey] as Node<TileData, ContainerData>;
    const boundingBox = target.data!.boundingBox;

    updateBoundingBox(leaf, boundingBox);

    if (target === root) {
        return leaf;
    }

    for (const node of enumerateNodes(root)) {
        if (isJoint(node)) {
            const currentJoint = node as Joint<TileData, ContainerData>;
            let removeKey: (keyof Joint<TileData, ContainerData>) | undefined;

            if (currentJoint.left === target) {
                removeKey = 'left';
            } else if (currentJoint.right === target) {
                removeKey = 'right';
            }

            if (removeKey !== undefined) {
                currentJoint[removeKey] = leaf as any;
                return root;
            }
        }
    }

    throw new Error('Joint not found');
}

export function insertToLargestTile(
    root: Node<TileData, ContainerData> | undefined,
    params: NewTileParams,
    activeTile?: TileData | undefined
): TreeOperationResult {
    const { key, name, args } = params;
    let largestTile: TileData | undefined;
    const tile = createTileData(key, name, args);

    // when root doesnt exist:
    if (root === undefined) {
        return {
            root: tile,
            modifiedTile: tile,
        };
    }

    // when root is the leaf:
    if (isLeaf(root)) {
        const rootJoint = createJoint(root as TileData, tile);

        return {
            root: rootJoint,
            modifiedTile: tile,
            modifiedJoint: rootJoint,
        };
    }

    if (!activeTile) {
        for (const currentTile of enumerateLeafs(root)) {
            if (largestTile === undefined || getBoxSquare(largestTile.box) < getBoxSquare(currentTile.box)) {
                largestTile = currentTile;
            }
        }
    } else {
        largestTile = activeTile;
    }

    const joint = insertToTile(root, largestTile!, tile);

    return {
        root,
        modifiedTile: tile,
        modifiedJoint: joint,
    };
}

export function insertToTile(
    root: Node<TileData, ContainerData>,
    target: TileData,
    tile: TileData,
    mosaic?: boolean,
    direction?: Direction
): Joint<TileData, ContainerData> {
    for (const node of enumerateNodes(root)) {
        if (isJoint(node)) {
            const currentJoint = node as Joint<TileData, ContainerData>;
            let leafName: keyof Joint<TileData, ContainerData> | undefined;

            if (currentJoint.left === target) {
                leafName = 'left';
            } else if (currentJoint.right === target) {
                leafName = 'right';
            }

            if (leafName !== undefined) {
                const joint = createJoint(target, tile, mosaic, direction);
                currentJoint[leafName] = joint as any;
                return joint;
            }
        }
    }

    throw new Error('Target not found.');
}

export function createJoint(
    sourceTile: TileData,
    newTile: TileData,
    mosaic?: boolean,
    directionForMosaic?: Direction
): Joint<TileData, ContainerData> {
    const height = getBoxHeight(sourceTile.box);
    const width = getBoxWidth(sourceTile.box);
    const direction = directionForMosaic ? directionForMosaic :
        height / 0.6 > width ? Direction.Vertical : Direction.Horizontal;
    let containerBox: Box | undefined;
    let leftTile = sourceTile;
    let rightTile = newTile;

    if (!mosaic) {
        if (direction === Direction.Horizontal) {
            // left & right modified
            newTile.box.right = sourceTile.box.right;
            sourceTile.box.right += width / 2.0;
            newTile.box.left = 100 - sourceTile.box.right;

            // top & bottom
            newTile.box.top = sourceTile.box.top;
            newTile.box.bottom = sourceTile.box.bottom;

            // container
            containerBox = {
                top: sourceTile.box.top,
                left: 100 - sourceTile.box.right,
                right: newTile.box.right,
                bottom: sourceTile.box.bottom,
            };
        } else if (direction === Direction.Vertical) {
            // top & bottom modified
            newTile.box.bottom = sourceTile.box.bottom;
            sourceTile.box.bottom += height / 2.0;
            newTile.box.top = 100 - sourceTile.box.bottom;

            // left & right
            newTile.box.left = sourceTile.box.left;
            newTile.box.right = sourceTile.box.right;
        } else {
            assertNever(direction);
        }
    } else {
        if (direction === Direction.Horizontal) {
            if (newTile.box.left > sourceTile.box.left) {
                rightTile = newTile;
                leftTile = sourceTile;
            } else {
                rightTile = sourceTile;
                leftTile = newTile;
            }
        } else if (direction === Direction.Vertical) {
            if (newTile.box.top > sourceTile.box.top) {
                rightTile = newTile;
                leftTile = sourceTile;
            } else {
                rightTile = sourceTile;
                leftTile = newTile;
            }
        }
    }

    return {
        left: leftTile,
        right: rightTile,
        data: {
            direction,
            boundingBox: mergeBoxes(sourceTile.box, newTile.box),
            ratio: 0.5,
            key: guid(),
        },
    };
}

export function createTileData(key: string, name: string, args?: any, box?: Box): TileData {
    return {
        key,
        name,
        args,
        box: box !== undefined ? box : createMaxBox(),
    };
}

export function createMaxBox(): Box {
    return {
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
    };
}

export function boxesAreEqual(box1: any, box2: any): boolean {
    return (
        box1.top === box2.top &&
        box1.bottom === box2.bottom &&
        box1.left === box2.left &&
        box1.right === box2.right
        );
}

function getBoxSquare(box: Box): number {
    return (100 - box.right - box.left) * (100 - box.bottom - box.top);
}

interface SplitBoxResult {
    left: Box;
    right: Box;
}

function splitBox(box: Box, ratio: number, direction: Direction): SplitBoxResult {
    if (direction === Direction.Horizontal) {
        const width = getBoxWidth(box);
        const split = width * ratio;
        return {
            left: {
                ...box,
                right: box.right + width - split,
            },
            right: {
                ...box,
                left: box.left + split,
            },
        };
    } else if (direction === Direction.Vertical) {
        const height = getBoxHeight(box);
        const split = height * ratio;
        return {
            left: {
                ...box,
                bottom: box.bottom + height - split,
            },
            right: {
                ...box,
                top: box.top + split,
            },
        };
    }

    throw new Error('Unknown direction');
}

function updateBoundingBox(node: Node<TileData, ContainerData>, box: Box): string[] {
    if (isLeaf(node)) {
        const leaf = node as Leaf<TileData>;
        leaf.box = box;
        return [leaf.key];
    } else {
        const joint = node as Joint<TileData, ContainerData>;
        const data = joint.data!;
        data.boundingBox = box;
        const { left, right } = splitBox(data.boundingBox, data.ratio, data.direction);
        const leftKeys = updateBoundingBox(joint.left, left);
        const rightKeys = updateBoundingBox(joint.right, right);
        return leftKeys.concat(rightKeys);
    }
}
